import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Competitor {
  id: string
  name: string
  website: string
  description: string
  markets: string[]
  products: string[]
  strengths: string[]
  weaknesses: string[]
  market_share: number
  threat_level: string
  last_updated: string
}

interface Insight {
  id: string
  type: string
  title: string
  description: string
  competitor_id: string | null
  impact: string
  created_at: string
}

export default function CompetitiveIntelligencePage() {
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [insights, setInsights] = useState<Insight[]>([])
  const [statistics, setStatistics] = useState<{total_competitors: number; avg_threat_level: number; total_insights: number; markets_covered: number} | null>(null)
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [compRes, insightsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/competitive-intel/competitors'),
      fetch(API_BASE + '/competitive-intel/insights'),
      fetch(API_BASE + '/competitive-intel/statistics')
    ])
    if (compRes.ok) setCompetitors((await compRes.json()).competitors || [])
    if (insightsRes.ok) setInsights((await insightsRes.json()).insights || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getThreatColor = (level: string) => {
    switch (level) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getImpactIcon = (impact: string) => {
    switch (impact) {
      case 'high': return '🔴'
      case 'medium': return '🟡'
      case 'low': return '🟢'
      default: return '⚪'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Competitive Intelligence</h1>

        {statistics && (
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Competitors Tracked</div>
              <div className="text-3xl font-bold">{statistics.total_competitors}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Markets Covered</div>
              <div className="text-3xl font-bold">{statistics.markets_covered}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Active Insights</div>
              <div className="text-3xl font-bold text-blue-600">{statistics.total_insights}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Avg Threat Level</div>
              <div className="text-3xl font-bold text-orange-600">{(statistics.avg_threat_level * 100).toFixed(0)}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-xl font-semibold">Competitor Overview</h2>
              </div>
              <div className="divide-y">
                {competitors.map(comp => (
                  <div
                    key={comp.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedCompetitor?.id === comp.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelectedCompetitor(comp)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">{comp.name}</div>
                        <div className="text-sm text-gray-500">{comp.markets.join(', ')}</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Market Share</div>
                          <div className="font-semibold">{comp.market_share}%</div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${getThreatColor(comp.threat_level)}`}>
                          {comp.threat_level} threat
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b">
                <h2 className="text-xl font-semibold">Latest Insights</h2>
              </div>
              <div className="divide-y">
                {insights.slice(0, 5).map(insight => (
                  <div key={insight.id} className="p-4">
                    <div className="flex items-start gap-3">
                      <span className="text-lg">{getImpactIcon(insight.impact)}</span>
                      <div>
                        <div className="font-medium">{insight.title}</div>
                        <div className="text-sm text-gray-600 mt-1">{insight.description}</div>
                        <div className="text-xs text-gray-400 mt-2">
                          {new Date(insight.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {selectedCompetitor ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedCompetitor.name}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500">Website</div>
                    <a href={selectedCompetitor.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {selectedCompetitor.website}
                    </a>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Description</div>
                    <div className="text-sm">{selectedCompetitor.description}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Strengths</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedCompetitor.strengths.map((s, i) => (
                        <span key={i} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Weaknesses</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedCompetitor.weaknesses.map((w, i) => (
                        <span key={i} className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">{w}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Products</div>
                    <div className="text-sm">{selectedCompetitor.products.join(', ')}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    + Add Competitor
                  </button>
                  <button className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">
                    Generate Report
                  </button>
                  <button className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50">
                    Market Analysis
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
